import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Button variants using class-variance-authority
 * Provides consistent styling across all button variants and sizes
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground active:bg-accent/80',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 rounded-md px-3',
        md: 'h-10 px-4 py-2',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render as a child component (useful for Next.js Link or React Router Link)
   */
  asChild?: boolean;
  /**
   * Display loading spinner and disable interactions
   */
  loading?: boolean;
  /**
   * Icon to display before the button text
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display after the button text
   */
  rightIcon?: React.ReactNode;
}

/**
 * Button Component
 *
 * A versatile button component with multiple variants, sizes, and loading states.
 * Fully accessible with keyboard navigation and screen reader support.
 *
 * @example
 * // Primary button
 * <Button>Save Patient Data</Button>
 *
 * @example
 * // Button with loading state
 * <Button loading={isSubmitting}>Submit Form</Button>
 *
 * @example
 * // Button with icons
 * <Button leftIcon={<PlusIcon />}>Add New</Button>
 * <Button rightIcon={<ArrowRightIcon />}>Continue</Button>
 *
 * @example
 * // Icon-only button
 * <Button size="icon" variant="ghost">
 *   <SearchIcon />
 * </Button>
 *
 * @example
 * // As a link component (with React Router or Next.js)
 * <Button asChild>
 *   <Link to="/patients">View Patients</Link>
 * </Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Show loading spinner in place of leftIcon when loading
    const displayLeftIcon = loading ? (
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
    ) : (
      leftIcon
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {displayLeftIcon && (
          <span className="inline-flex shrink-0">{displayLeftIcon}</span>
        )}
        {children}
        {rightIcon && !loading && (
          <span className="inline-flex shrink-0">{rightIcon}</span>
        )}
        {loading && <span className="sr-only">Loading...</span>}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
